/**
 * Guitar-mode "stage bus" — compressor + soft saturator + parallel
 * reverb send spliced between the SFX bus and the master output.
 * Encapsulated as a class so the audio engine can opt-in per mode
 * (Guitar enables it; Piano + Circles keep their dry path) and tear
 * it down cleanly when the session ends.
 *
 * Signal flow once enabled:
 *
 *     sfxBus → [input gain] → [compressor] → [soft saturator] ┬→ dryGain → master
 *                                                              └→ convolver → wetGain → master
 *
 * The compressor glues the layered hit-click + chord stab + power
 * chord stack so they sit together instead of stacking transients.
 * The soft saturator adds a touch of harmonic warmth (the "amp" of
 * the stage). The convolver adds a short ambient tail (~220 ms) so
 * hits read as "played in a venue" rather than "synthesised in a
 * vacuum".
 */

import { buildReverbImpulse, createSoftSaturatorCurve } from './guitarSfx';

/** Dry-path gain — left at unity; sliders live on the SFX bus upstream. */
const STAGE_DRY_GAIN = 1.0;

/** Reverb send level. Quiet on purpose; this is ambience, not effect. */
const STAGE_WET_GAIN = 0.18;

/** Compressor settings tuned for "make the SFX layers sit together". */
const COMP_THRESHOLD_DB = -10;
const COMP_KNEE_DB = 22;
const COMP_RATIO = 3.2;
const COMP_ATTACK_S = 0.004;
const COMP_RELEASE_S = 0.18;

/** Saturator drive — low enough to be transparent on quiet hits, audible on loud stacks. */
const SATURATOR_DRIVE = 0.22;

/**
 * Wraps the compressor + saturator + reverb chain. The audio engine
 * splices it in by disconnecting `sfxBus → master` and reconnecting
 * `sfxBus → stageBus.input`; the chain owns the rest of the path
 * down to `master`.
 */
export class GuitarStageBus {
  private ctx: AudioContext;
  private master: AudioNode;
  private input: GainNode;
  private compressor: DynamicsCompressorNode;
  private saturator: WaveShaperNode;
  private dryGain: GainNode;
  private wetGain: GainNode;
  private convolver: ConvolverNode | null = null;

  constructor(ctx: AudioContext, master: AudioNode) {
    this.ctx = ctx;
    this.master = master;
    this.input = ctx.createGain();
    this.input.gain.value = 1.0;

    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = COMP_THRESHOLD_DB;
    this.compressor.knee.value = COMP_KNEE_DB;
    this.compressor.ratio.value = COMP_RATIO;
    this.compressor.attack.value = COMP_ATTACK_S;
    this.compressor.release.value = COMP_RELEASE_S;

    this.saturator = ctx.createWaveShaper();
    this.saturator.curve = createSoftSaturatorCurve(SATURATOR_DRIVE);
    this.saturator.oversample = '2x';

    this.dryGain = ctx.createGain();
    this.dryGain.gain.value = STAGE_DRY_GAIN;

    this.wetGain = ctx.createGain();
    this.wetGain.gain.value = 0;

    this.input.connect(this.compressor);
    this.compressor.connect(this.saturator);
    this.saturator.connect(this.dryGain);
    this.dryGain.connect(this.master);

    this.wetGain.connect(this.master);

    this.tryAttachConvolver();
  }

  /**
   * Head of the chain — splice point for the audio engine. Connect
   * the SFX bus here after disconnecting its direct master link.
   */
  getInput(): AudioNode {
    return this.input;
  }

  /**
   * Briefly mute the reverb send so the next loop starts crisp
   * rather than carrying the previous loop's tail. Restores the
   * normal wet level once the blanking window passes.
   */
  silenceReverbTail(): void {
    if (!this.convolver) {
      return;
    }
    const now = this.ctx.currentTime;
    try {
      const wet = this.wetGain.gain;
      const startValue = Math.max(wet.value, 0.0001);
      wet.cancelScheduledValues(now);
      wet.setValueAtTime(startValue, now);
      wet.linearRampToValueAtTime(0.0001, now + 0.04);
      wet.linearRampToValueAtTime(STAGE_WET_GAIN, now + 0.22);
    } catch (err) {
      console.warn('GuitarStageBus.silenceReverbTail failed:', err);
    }
  }

  /** Best-effort teardown. Called from `AudioEngine.stop()`. */
  destroy(): void {
    try {
      this.input.disconnect();
    } catch (err) {
      console.warn('GuitarStageBus.destroy: input disconnect failed:', err);
    }
    try {
      this.compressor.disconnect();
    } catch (err) {
      console.warn('GuitarStageBus.destroy: compressor disconnect failed:', err);
    }
    try {
      this.saturator.disconnect();
    } catch (err) {
      console.warn('GuitarStageBus.destroy: saturator disconnect failed:', err);
    }
    try {
      this.dryGain.disconnect();
    } catch (err) {
      console.warn('GuitarStageBus.destroy: dryGain disconnect failed:', err);
    }
    try {
      this.wetGain.disconnect();
    } catch (err) {
      console.warn('GuitarStageBus.destroy: wetGain disconnect failed:', err);
    }
    if (this.convolver) {
      try {
        this.convolver.disconnect();
      } catch (err) {
        console.warn('GuitarStageBus.destroy: convolver disconnect failed:', err);
      }
    }
  }

  /**
   * The ConvolverNode build is wrapped because very old Safari + some
   * locked-down WebViews omit `createConvolver` or reject buffer
   * assignment. We fall back to a dry-only stage rather than throwing
   * — the compressor + saturator still glue the SFX layer together.
   */
  private tryAttachConvolver(): void {
    if (typeof this.ctx.createConvolver !== 'function') {
      return;
    }
    try {
      const convolver = this.ctx.createConvolver();
      convolver.buffer = buildReverbImpulse(this.ctx, 0.22, 3.5);
      this.saturator.connect(convolver);
      convolver.connect(this.wetGain);
      this.wetGain.gain.value = STAGE_WET_GAIN;
      this.convolver = convolver;
    } catch (err) {
      console.warn('GuitarStageBus: convolver setup failed; reverb disabled', err);
      this.convolver = null;
    }
  }
}
